import React, { useEffect, useMemo, useState } from "react";
import { loadConvo } from "@/utils/chatStorage";

const ChatSidebar = ({
  isOpen,
  onToggle,
  conversations,
  activeId,
  onCreateChat,
  onSelectChat,
  onRemoveChat,
  onRenameChat,
  searchInContent = true,
}) => {
  const [query, setQuery] = useState("");
  const [contentIndex, setContentIndex] = useState({}); 

  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");


  // Build content index for search-in-content
  
  useEffect(() => {
    if (!searchInContent) {
      setContentIndex({});
      return;
    }

    (async () => {
      const nextIndex = {};
      for (const c of conversations || []) {
        try {
          const full = loadConvo(c.id); // { id, title, messages, ... }

          if (full?.messages?.length) {
            const blob = full.messages
              .map((m) => m?.content?.[0]?.text || "")
              .filter(Boolean)
              .join(" \n ")
              .toLowerCase();
            nextIndex[c.id] = blob;
          } else {
            nextIndex[c.id] = "";
          }
        } catch {
          nextIndex[c.id] = "";
        }
      }
      setContentIndex(nextIndex);
    })();
  }, [conversations, searchInContent]);

 
  // Filtering (by title + optional content)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;

    return conversations.filter((c) => {
      const titleHit =
        c.title?.toLowerCase?.().includes(q) ||
        (c.id && c.id.toLowerCase().includes(q));
      if (titleHit) return true;

      if (!searchInContent) return false;
      const content = contentIndex[c.id] || "";
      return content.includes(q);
    });
  }, [query, conversations, contentIndex, searchInContent]);

  
  // Rename logic (triggered by icon)
 
  const startEditing = (conversation) => {
    setEditingId(conversation.id);
    setEditingTitle(conversation.title || "");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingTitle("");
  };

  const submitEditing = () => {
    if (!editingId) return;
    const trimmed = editingTitle.trim();
    if (trimmed && onRenameChat) {
      onRenameChat(editingId, trimmed);
    }
    cancelEditing();
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitEditing();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditing();
    }
  };

  return (
    <aside
      aria-label="Chat sidebar"
      className={`chat-sidebar ${isOpen ? "open" : "closed"}`}
      style={{
        position: "fixed",
        top: 0,
        left: 100, 
        height: "100vh",
        width: isOpen ? 320 : 56,
        transition: "width 180ms ease",
        background: "#ffffff",
        borderRight: "1px solid #e9ecef",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Toggle button (always visible) */}
      <button
        aria-label={isOpen ? "Close sidebar" : "Open sidebar"}
        onClick={onToggle}
        style={{
          position: "absolute",
          top: 12,
          right: -16,
          width: 32,
          height: 32,
          borderRadius: 16,
          border: "1px solid #e9ecef",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
        }}
        title={isOpen ? "Close" : "Open"}
      >
        {isOpen ? (
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="#111"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M3 6h18M3 12h18M3 18h18"
              stroke="#111"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>

      {/* Collapsed rail content (icon column) */}
      {!isOpen && (
        <div
          style={{
            marginTop: 56,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button
            title="New chat"
            aria-label="New chat"
            onClick={onCreateChat}
            style={railIconButtonStyle}
          >
            <PlusIcon />
          </button>
        </div>
      )}

      {/* Expanded content */}
      {isOpen && (
        <>
          {/* Header */}
          <div
            style={{
              padding: "14px 16px 10px",
              borderBottom: "1px solid #f1f3f5",
            }}
          >
            <div style={{ fontWeight: 600 }}>Conversations</div>
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
              <div style={{ position: "relative", flex: 1 }}>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  aria-label="Search conversations"
                  style={{
                    width: "100%",
                    height: 36,
                    borderRadius: 8,
                    border: "1px solid #dee2e6",
                    padding: "0 32px 0 36px",
                    outline: "none",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 8,
                    opacity: 0.6,
                  }}
                  aria-hidden="true"
                >
                  <SearchIcon />
                </div>
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    style={{
                      position: "absolute",
                      right: 6,
                      top: 6,
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      opacity: 0.7,
                    }}
                  >
                    <CloseSmallIcon />
                  </button>
                )}
              </div>
              <button
                onClick={onCreateChat}
                className="btn btn-primary"
                style={{
                  height: 36,
                  borderRadius: 8,
                  padding: "0 12px",
                  border: "1px solid #0d6efd",
                  background: "#0d6efd",
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  <PlusIcon />
                  New
                </span>
              </button>
            </div>
          </div>

          {/* List */}
          <div
            role="list"
            aria-label="Previous conversations"
            style={{
              padding: 8,
              overflowY: "auto",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              scrollBehavior: "smooth",
              maxHeight: "calc(100vh - 100px)",
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  fontSize: 14,
                  color: "#6c757d",
                  padding: "16px 8px",
                }}
              >
                {query ? "No matches." : "No conversations yet."}
              </div>
            ) : (
              filtered.map((c) => (
                <div
                  key={c.id}
                  role="listitem"
                  className={`conv-item ${c.id === activeId ? "active" : ""}`}
                  onClick={() => onSelectChat(c.id)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onSelectChat(c.id);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 10px",
                    borderRadius: 10,
                    border:
                      c.id === activeId
                        ? "1px solid #0d6efd"
                        : "1px solid transparent",
                    background: c.id === activeId ? "#e7f1ff" : "transparent",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: "#f1f3f5",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ChatIcon />
                  </div>

                  {/* Title + timestamp (with rename input) */}
                  <div
                    style={{
                      minWidth: 0,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {editingId === c.id ? (
                      <input
                        autoFocus
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onBlur={submitEditing}
                        onKeyDown={handleTitleKeyDown}
                        style={{
                          width: "100%",
                          fontSize: 14,
                          fontWeight: 600,
                          borderRadius: 6,
                          border: "1px solid #ced4da",
                          padding: "2px 6px",
                          outline: "none",
                        }}
                        placeholder="Chat title"
                      />
                    ) : (
                      <div
                        title={c.title}
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {c.title || "Untitled chat"}
                      </div>
                    )}

                    {c.updatedAt ? (
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6c757d",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {new Date(c.updatedAt).toLocaleString()}
                      </div>
                    ) : null}
                  </div>

                  {/* Edit icon */}
                  <button
                    aria-label={`Rename "${c.title || c.id}"`}
                    onClick={(e) => {
                      e.stopPropagation(); 
                      startEditing(c);
                    }}
                    title="Rename chat"
                    style={ghostIconButtonStyle}
                  >
                    <EditIcon />
                  </button>

                  {/* Delete icon */}
                  <button
                    aria-label={`Delete "${c.title || c.id}"`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveChat(c.id);
                    }}
                    title="Remove chat"
                    style={{ ...ghostIconButtonStyle, marginLeft: 4 }}
                  >
                    <TrashIcon />
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </aside>
  );
};

/* --- Small inline icon components (no extra deps) --- */
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      d="M12 5v14M5 12h14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);
const CloseSmallIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const ChatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24">
    <path
      d="M21 15a3 3 0 0 1-3 3H8l-5 4V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v9z"
      fill="currentColor"
    />
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      d="M3 6h18M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6M10 10v8M14 10v8M9 6l1-2h4l1 2"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);
const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path
      d="M4 20h4l10.5-10.5a1.5 1.5 0 0 0-2.12-2.12L5.88 17.88 4 20z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.5 5.5l4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* --- Small shared styles --- */
const railIconButtonStyle = {
  width: 36,
  height: 36,
  borderRadius: 10,
  border: "1px solid #e9ecef",
  background: "#fff",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const ghostIconButtonStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid #e9ecef",
  background: "#fff",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

export default ChatSidebar;
